import {
  type BondingCurveBuyQuote,
  type BondingCurveFeeConfig,
  type BondingCurveSellQuote,
  type BondingCurveState,
  bondingCurveSpotPriceD9,
  quoteBondingCurveBuyExactIn,
  quoteBondingCurveBuyForQuoteBudget,
  quoteBondingCurveSellExactIn,
  quoteBondingCurveSellForQuoteOutput,
} from './bonding-curve.js';
import {
  advanceBondingCurveConviction,
  type BondingCurveConvictionState,
  CONVICTION_SCALE_PPB,
  evaluateBondingCurveConviction,
} from './conviction.js';
import {
  type CpSwapPoolState,
  type CpSwapQuote,
  cpSwapSpotPriceD9,
  quoteCpSwapBuyExactIn,
  quoteCpSwapSellExactIn,
  quoteCpSwapSellFeeExcludedExactIn,
  quoteCpSwapSellForQuoteOutput,
} from './cp-swap.js';
import { isNonNegativeI64, isRate, isU64, unavailable } from './integer.js';
import { isPenaltySell, type PenaltyTarget, resolvePenaltyTarget } from './penalty.js';
import type {
  ChainClock,
  PoolMathResult,
  QuoteVenue,
  SellQuoteOutput,
  VenueResolutionInput,
} from './types.js';
import { resolveQuoteVenue } from './venue.js';

export interface BondingCurvePoolQuoteState {
  state: BondingCurveState;
  fees: BondingCurveFeeConfig;
  /** Current config margin above the rounded neutral conviction share. */
  convictionReferenceMarginPpb: bigint;
  /** Actual outcome identity for each position in the conviction vectors. */
  convictionOutcomeIndices?: readonly number[];
  /** Indexed anchor shared by the existing eligibility gate and current penalty policy. */
  conviction: BondingCurveConvictionState;
}

interface OutcomePoolChainContext {
  chainClock: ChainClock;
  /** Explicit time used for quote eligibility and sell-penalty evaluation. */
  quoteUnixTimestampSeconds?: bigint;
}

export type OutcomePoolQuoteContext = OutcomePoolChainContext &
  (
    | {
        poolModel: 'bonding_curve';
        venue: VenueResolutionInput;
        bondingCurve: BondingCurvePoolQuoteState;
      }
    | {
        poolModel: 'cp_swap';
        venue: VenueResolutionInput;
        cpSwap: CpSwapPoolState;
      }
    | {
        poolModel: 'missing';
        venue: VenueResolutionInput;
      }
    | {
        poolModel: 'stale';
        venue: VenueResolutionInput;
      }
  );

export type OutcomeBuyQuote = BondingCurveBuyQuote | CpSwapQuote;
export type OutcomeSellQuote = BondingCurveSellQuote | CpSwapQuote;

export interface OutcomeBuyExactInInput {
  context: OutcomePoolQuoteContext;
  quoteAmountInD6: bigint;
}

export interface OutcomeBuyForQuoteBudgetInput {
  context: OutcomePoolQuoteContext;
  maximumQuoteDebitD6: bigint;
}

export interface OutcomeSellExactInInput {
  context: OutcomePoolQuoteContext;
  outcomeAmountInD6: bigint;
}

export interface OutcomeSellForQuoteOutputInput {
  context: OutcomePoolQuoteContext;
  targetQuoteOutputD6: bigint;
  target: SellQuoteOutput;
  maximumOutcomeAmountInD6: bigint;
}

export function resolveOutcomePoolQuoteVenue(
  context: OutcomePoolQuoteContext,
): PoolMathResult<QuoteVenue> {
  const venue = resolveQuoteVenue(context.venue);
  if (!venue.available) return venue;
  if (context.poolModel === 'stale') return unavailable('stale_pool_state');
  if (venue.value === 'bonding_curve' && context.poolModel !== 'bonding_curve') {
    return unavailable('missing_bonding_curve_state');
  }
  if (venue.value === 'cp_swap' && context.poolModel !== 'cp_swap') {
    return unavailable('missing_cp_swap_state');
  }
  return venue;
}

export function quoteOutcomeBuyExactIn(
  input: OutcomeBuyExactInInput,
): PoolMathResult<OutcomeBuyQuote> {
  const venue = resolveOutcomePoolQuoteVenue(input.context);
  if (!venue.available) return venue;

  if (venue.value === 'bonding_curve') {
    if (input.context.poolModel !== 'bonding_curve') {
      return unavailable('missing_bonding_curve_state');
    }
    const chainTime = quoteChainTime(input.context);
    if (!chainTime.available) return chainTime;
    const gate = validateBondingCurveTradeGate(
      input.context.bondingCurve.conviction,
      chainTime.value,
      input.context.quoteUnixTimestampSeconds,
    );
    if (!gate.available) return gate;
    return quoteBondingCurveBuyExactIn({
      state: input.context.bondingCurve.state,
      fees: input.context.bondingCurve.fees,
      quoteAmountInD6: input.quoteAmountInD6,
    });
  }
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  return quoteCpSwapBuyExactIn({
    state: input.context.cpSwap,
    quoteAmountInD6: input.quoteAmountInD6,
  });
}

/** Quotes the largest executable exact buy whose actual wallet debit fits the budget. */
export function quoteOutcomeBuyForQuoteBudget(
  input: OutcomeBuyForQuoteBudgetInput,
): PoolMathResult<OutcomeBuyQuote> {
  const venue = resolveOutcomePoolQuoteVenue(input.context);
  if (!venue.available) return venue;

  if (venue.value === 'bonding_curve') {
    if (input.context.poolModel !== 'bonding_curve') {
      return unavailable('missing_bonding_curve_state');
    }
    const chainTime = quoteChainTime(input.context);
    if (!chainTime.available) return chainTime;
    const gate = validateBondingCurveTradeGate(
      input.context.bondingCurve.conviction,
      chainTime.value,
      input.context.quoteUnixTimestampSeconds,
    );
    if (!gate.available) return gate;
    return quoteBondingCurveBuyForQuoteBudget({
      state: input.context.bondingCurve.state,
      fees: input.context.bondingCurve.fees,
      maximumQuoteDebitD6: input.maximumQuoteDebitD6,
    });
  }
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  return quoteCpSwapBuyExactIn({
    state: input.context.cpSwap,
    quoteAmountInD6: input.maximumQuoteDebitD6,
  });
}

export function quoteOutcomeSellExactIn(
  input: OutcomeSellExactInInput,
): PoolMathResult<OutcomeSellQuote> {
  const chainTime = quoteChainTime(input.context);
  if (!chainTime.available) return chainTime;
  const venue = resolveOutcomePoolQuoteVenue(input.context);
  if (!venue.available) return venue;

  if (venue.value === 'bonding_curve') {
    if (input.context.poolModel !== 'bonding_curve') {
      return unavailable('missing_bonding_curve_state');
    }
    const gate = validateBondingCurveTradeGate(
      input.context.bondingCurve.conviction,
      chainTime.value,
      input.context.quoteUnixTimestampSeconds,
    );
    if (!gate.available) return gate;
    const penalty = penaltyTargetAtQuoteTime(input.context);
    if (!penalty.available) return penalty;
    return quoteBondingCurveSellExactIn({
      state: input.context.bondingCurve.state,
      fees: input.context.bondingCurve.fees,
      outcomeAmountInD6: input.outcomeAmountInD6,
      penaltyApplied: isPenaltySell(
        penalty.value.targetOutcomeIndex,
        input.context.venue.outcomeIndex,
      ),
    });
  }
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  return quoteCpSwapSellExactIn({
    state: input.context.cpSwap,
    outcomeAmountInD6: input.outcomeAmountInD6,
    nowUnixSeconds: chainTime.value,
  });
}

/**
 * Marks an exact outcome sell without trading fees. This is a P&L valuation,
 * not an executable receipt or a slippage-protection quote.
 */
export function quoteOutcomeSellFeeExcludedExactIn(
  input: OutcomeSellExactInInput,
): PoolMathResult<bigint> {
  const quote = quoteOutcomeSellExactIn(input);
  if (!quote.available) return quote;
  if (quote.value.venue === 'bonding_curve') {
    return { available: true, value: quote.value.grossQuoteOutputD6 };
  }
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  const feeExcludedQuote = quoteCpSwapSellFeeExcludedExactIn({
    state: input.context.cpSwap,
    outcomeAmountInD6: input.outcomeAmountInD6,
    nowUnixSeconds: input.context.chainClock.unixTimestampSeconds,
  });
  if (!feeExcludedQuote.available) return feeExcludedQuote;
  return { available: true, value: feeExcludedQuote.value.outputAmountD6 };
}

export function quoteOutcomeSellForQuoteOutput(
  input: OutcomeSellForQuoteOutputInput,
): PoolMathResult<OutcomeSellQuote> {
  const chainTime = quoteChainTime(input.context);
  if (!chainTime.available) return chainTime;
  const venue = resolveOutcomePoolQuoteVenue(input.context);
  if (!venue.available) return venue;

  if (venue.value === 'bonding_curve') {
    if (input.context.poolModel !== 'bonding_curve') {
      return unavailable('missing_bonding_curve_state');
    }
    const gate = validateBondingCurveTradeGate(
      input.context.bondingCurve.conviction,
      chainTime.value,
      input.context.quoteUnixTimestampSeconds,
    );
    if (!gate.available) return gate;
    const penalty = penaltyTargetAtQuoteTime(input.context);
    if (!penalty.available) return penalty;
    return quoteBondingCurveSellForQuoteOutput({
      state: input.context.bondingCurve.state,
      fees: input.context.bondingCurve.fees,
      targetQuoteOutputD6: input.targetQuoteOutputD6,
      target: input.target,
      maximumOutcomeAmountInD6: input.maximumOutcomeAmountInD6,
      penaltyApplied: isPenaltySell(
        penalty.value.targetOutcomeIndex,
        input.context.venue.outcomeIndex,
      ),
    });
  }
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  return quoteCpSwapSellForQuoteOutput({
    state: input.context.cpSwap,
    targetQuoteOutputD6: input.targetQuoteOutputD6,
    target: input.target,
    maximumOutcomeAmountInD6: input.maximumOutcomeAmountInD6,
    nowUnixSeconds: chainTime.value,
  });
}

export function outcomeSpotPriceD9(context: OutcomePoolQuoteContext): PoolMathResult<bigint> {
  const venue = resolveOutcomePoolQuoteVenue(context);
  if (!venue.available) return venue;
  if (venue.value === 'bonding_curve') {
    if (context.poolModel !== 'bonding_curve') {
      return unavailable('missing_bonding_curve_state');
    }
    return bondingCurveSpotPriceD9(context.bondingCurve.state);
  }
  if (context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  return cpSwapSpotPriceD9(context.cpSwap);
}

function quoteChainTime(context: OutcomePoolQuoteContext): PoolMathResult<bigint> {
  if (!isU64(context.chainClock.slot) || !isU64(context.chainClock.unixTimestampSeconds)) {
    return unavailable('invalid_amount');
  }
  return { available: true, value: context.chainClock.unixTimestampSeconds };
}

function validateBondingCurveTradeGate(
  state: BondingCurveConvictionState,
  chainUnixSeconds: bigint,
  quoteUnixSeconds: bigint | undefined,
): PoolMathResult<true> {
  if (state.timedTradingWindow !== undefined) {
    const evaluationTime = quoteUnixSeconds ?? chainUnixSeconds;
    const { startUnixSeconds, endUnixSeconds } = state.timedTradingWindow;
    if (!isU64(evaluationTime) || !isU64(startUnixSeconds) || !isU64(endUnixSeconds))
      return unavailable('invalid_amount');
    if (startUnixSeconds >= endUnixSeconds) return unavailable('invalid_conviction_state');
    if (evaluationTime < startUnixSeconds || evaluationTime >= endUnixSeconds)
      return unavailable('market_not_quotable');
    return { available: true, value: true };
  }
  if (state.resolutionAtUnixSeconds !== undefined) {
    const evaluationTime = quoteUnixSeconds ?? chainUnixSeconds;
    if (!isU64(evaluationTime)) return unavailable('invalid_amount');
    if (state.resolutionAtUnixSeconds !== null && evaluationTime >= state.resolutionAtUnixSeconds) {
      return unavailable('resolution_threshold_reached');
    }
    return { available: true, value: true };
  }
  const evaluation = evaluateBondingCurveConviction(state, chainUnixSeconds);
  if (!evaluation.available) return evaluation;
  if (evaluation.value.resolutionWinnerOutcomeIndex !== null) {
    return unavailable('resolution_threshold_reached');
  }
  return { available: true, value: true };
}

export function penaltyTargetAtQuoteTime(
  context: Extract<OutcomePoolQuoteContext, { poolModel: 'bonding_curve' }>,
): PoolMathResult<PenaltyTarget> {
  const state = context.bondingCurve.conviction;
  const quoteTime = context.quoteUnixTimestampSeconds ?? context.chainClock.unixTimestampSeconds;
  if (
    !isNonNegativeI64(quoteTime) ||
    !isNonNegativeI64(state.lastConvictionUpdateUnixSeconds) ||
    state.outcomeConvictionPpb.some((value) => !isRate(value, CONVICTION_SCALE_PPB))
  )
    return unavailable('invalid_conviction_state');
  const conviction = advanceBondingCurveConviction(state, quoteTime);
  if (!conviction.available) return conviction;
  return resolvePenaltyTarget(
    conviction.value.updatedOutcomeConvictionPpb,
    context.bondingCurve.convictionReferenceMarginPpb,
    context.bondingCurve.convictionOutcomeIndices,
  );
}
